import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { authAPI, recipesAPI, messagesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RecipeCard from '../common/RecipeCard';
import './Profile.css';

export default function ProfilePage() {
  const { id } = useParams();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [watchingUsers, setWatchingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWatching, setIsWatching] = useState(false);

  const isOwn = user?._id === id;

  useEffect(() => {
    const load = async () => {
      try {
        const [profileData, recipesData] = await Promise.all([
          authAPI.getUser(id),
          recipesAPI.getAll({ userId: id, limit: 50 }),
        ]);
        setProfile(profileData.user);
        setRecipes(recipesData.recipes);
        setIsWatching(user?.watching?.some(w => (w._id || w).toString() === id));

        if (user?._id === id) {
          try {
            const watchData = await authAPI.getWatching();
            setWatchingUsers(watchData.watching || []);
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [id, user?.watching, user?._id]);

  const handleWatch = async () => {
    try {
      const data = await authAPI.toggleWatch(id);
      setIsWatching(data.watching);
      updateUser(data.user);
    } catch { /* ignore */ }
  };

  const handleMessage = async () => {
    try {
      const data = await messagesAPI.getOrCreateConversation(id);
      navigate(`/messages/${data.conversation._id}`);
    } catch { /* ignore */ }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!profile) return <div className="empty-state"><h3>User not found</h3></div>;

  return (
    <div className="profile-page fade-in">
      <div className="profile-header card">
        <div className="profile-avatar-lg">
          {profile.profilePicture ? (
            <img src={profile.profilePicture} alt={profile.username} />
          ) : (
            <span>{profile.username[0].toUpperCase()}</span>
          )}
        </div>
        <div className="profile-info">
          <h1>{profile.username}</h1>
          {profile.about && <p className="profile-about">{profile.about}</p>}
          <div className="profile-stats">
            <div className="stat"><strong>{recipes.length}</strong> recipes</div>
            <div className="stat"><strong>{profile.followersCount || 0}</strong> followers</div>
            <div className="stat"><strong>{profile.followingCount || 0}</strong> following</div>
            <div className="stat"><strong>{new Date(profile.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</strong> joined</div>
          </div>
        </div>
        {!isOwn && user && (
          <div className="profile-actions">
            <button className={`btn ${isWatching ? 'btn-primary' : 'btn-outline'}`} onClick={handleWatch}>
              {isWatching ? 'Watching' : 'Watch'}
            </button>
            <button className="btn btn-outline" onClick={handleMessage}>
              ✉ Message
            </button>
          </div>
        )}
      </div>

      {isOwn && watchingUsers.length > 0 && (
        <div className="following-section mt-3">
          <div className="page-header">
            <h2>Following</h2>
          </div>
          <div className="following-list card">
            {watchingUsers.map(u => (
              <Link key={u._id} to={`/profile/${u._id}`} className="following-item">
                <div className="following-avatar">
                  {u.profilePicture ? (
                    <img src={u.profilePicture} alt={u.username} />
                  ) : (
                    <span>{u.username[0].toUpperCase()}</span>
                  )}
                </div>
                <span className="following-name">{u.username}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="page-header mt-3">
        <h2>{isOwn ? 'My Recipes' : `${profile.username}'s Recipes`}</h2>
      </div>

      {recipes.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📝</span>
          <h3>No recipes yet</h3>
        </div>
      ) : (
        <div className="recipe-grid">
          {recipes.map(r => <RecipeCard key={r._id} recipe={r} />)}
        </div>
      )}
    </div>
  );
}
